import React from 'react';
import { html } from 'lit';
import parse from 'html-react-parser';
import { filter } from 'omni-ui/icon/iconset-interactive.js';

// Shared components and utilities
import { 
    OmniSearchComponent, 
    OmniTableComponent, 
    OmniTileComponent, 
    OmniToolbarComponent 
} from '../../../../shared/components/omni-components';
import { Utils, NoDataComponent, AlertMessagesService } from '../../../../shared';

// Feature imports
import { ObjectsHierarchyService } from '../../';
import HierarchyFilterComponent from './hierarchyFilterComponent';
export class ObjectsHierarchyComponent extends React.Component {

    defaultState = {
        tableData: [],
        showSkeleton: false,
    }
    
    data = [];
    totalCount = 0;
    sort = { key: '', dir: '' };
    columnOptions = [];

    constructor(props) {
        super(props);
        this.state = this.defaultState;
    }

    // Lifecycle method to fetch the data from the server
    componentDidMount() {
        this.props.setSpinnerCount(0)
        this.getHierarchyColumnOptions();
        this.fetchHierarchyTemplatesData();
    }

    // Lifecycle method to reset the state
    displaySkeletonTable() {
        let rows = '';
        for (let row = 0; row < 11; row++) {
            rows = rows + '<div className="table skeleton"></div>';
        }
        rows = (html`${rows}`);
        return <>{parse(rows.values[0])}</>;
    }

     /**
     * Fetches the hierarchy column options from the ObjectsHierarchyService.
     */
    getHierarchyColumnOptions = () => {
        ObjectsHierarchyService.getHierarchyColumnOptions()
            .then((response) => {
                this.columnOptions = response.data;
            })
            .catch((error) => {
                AlertMessagesService.showErrorToastMessage(error); // Display an error toast message if the API call fails
            });
    };

    /**
     * Fetches the hierarchy templates data from the ObjectsHierarchyService.
     */
    fetchHierarchyTemplatesData = () => {
        this.setState({ showSkeleton: true }); // Set the showSkeleton state to true to indicate loading
        ObjectsHierarchyService.getAllHierarchyData() // Make an API call to retrieve all hierarchy data
            .then((response) => { // Process the response data using the modifiedTemplateData method
                const templates = this.modifiedTemplateData(response.data); // Update the component's state with the processed data and hide the skeleton loader
                let defaultTamplate = templates.filter(val=> val.clientName === 'default'); 
                let otherTamplate = templates.filter(val=> val.clientName !== 'default'); 
                const allTemplates = [...defaultTamplate, ...otherTamplate]; // Combine the default and other templates
                this.data = allTemplates; 
                this.totalCount = templates.length;
                this.setState({ tableData: allTemplates, showSkeleton: false });
            })
            .catch(() => { // Hide the skeleton loader and display an error alert message if the API call fails
                this.setState({ showSkeleton: false });
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: this.fetchHierarchyTemplatesData, title: 'Hierarchy API cannot be loaded', bodyMsgArr: [] }]);
            });
    };

    /**
     * Modifies the template data by creating and sorting hierarchy objects.
     * @param {Array} templateData - The array of template data to be processed.
     * @returns {Array} The modified template data with sorted hierarchy objects.
     */
    modifiedTemplateData = (templateData) => {
        return templateData?.map((template) => {
            let hierarchyObjects = [];
            hierarchyObjects = this.createHierarchyObjectsArray(template?.columns); 
            hierarchyObjects.sort((objectA, objectB) => objectA.level - objectB.level);
            return { ...template, hierarchyObjects };
        });
    };

    /**
     * Creates an array of hierarchy objects from the provided template columns data.
     * This method processes the template columns data to group columns into hierarchy objects based on their tier and tier order.
     * It distinguishes between minimum set columns and custom columns, and ensures that columns are not duplicated within their respective groups.
     * @param {Array} templateColumnsData - The array of template columns data to be processed.
     * @returns {Array} The array of hierarchy objects with grouped columns.
     */
    createHierarchyObjectsArray = (templateColumnsData = []) => {
        return templateColumnsData.reduce((accumulatorArray, currentObject) => {
            const existingGroup = accumulatorArray.find((group) => group?.type === currentObject?.tier && group?.level === currentObject?.tierOrder);
            const existingColumn = this.findExistingColumn(existingGroup?.hierarchyObjectColumns, currentObject);
            const existingCustomColumn = this.findExistingColumn(existingGroup?.hierarchyObjectCustomColumns, currentObject);
            if (currentObject?.isMinSetColumn) {
                this.columnsGrouping(existingGroup, existingColumn, currentObject, accumulatorArray);
            } else {
                this.columnsGrouping(existingGroup, existingCustomColumn, currentObject, accumulatorArray, true);
            }
            return accumulatorArray;
        }, []);
    };

    /**
     * Finds an existing column within a group based on the provided data and current object.
     * @param {Array} data - The array of columns data to be searched.
     * @param {Object} currentObject - The current column object to be compared.
     * @returns {Object} The existing column object if found, otherwise null.
     */
    findExistingColumn = (data, currentObject) => {
        return data?.find((column) => column?.tier === currentObject?.tier && column?.tierOrder === currentObject?.tierOrder && column?.columnName === currentObject?.columnName);
    };

    /**
     * Groups columns based on the provided data and current object.
     * If an existing group is found, the current object is added to the group's columns array.
     * If an existing column is found, the current object is not added to the group's columns array.
     * If no existing group is found, a new group is created with the current object.
     * @param {Object} existingGroup - The existing group object to be checked.
     * @param {Object} existingColumn - The existing column object to be checked.
     * @param {Object} currentObject - The current column object to be added.
     * @param {Array} accumulatorArray - The array of groups to be updated.
     * @param {Boolean} isCustomColumn - The flag to indicate whether the current object is a custom column.
     * @returns {void}
     */
    columnsGrouping = (existingGroup, existingColumn, currentObject, accumulatorArray, isCustomColumn = false) => {
        if(existingGroup){
            if(!existingColumn){
                existingGroup[isCustomColumn ? 'hierarchyObjectCustomColumns' : 'hierarchyObjectColumns'].push(currentObject);
            }
        } else {
            const hierarchyObjectColumns = isCustomColumn ? [] : [currentObject];
            const hierarchyObjectCustomColumns = isCustomColumn ? [currentObject] : [];
            accumulatorArray.push({ type: currentObject?.tier, level: currentObject?.tierOrder, hierarchyObjectColumns, hierarchyObjectCustomColumns });
        }
    };

    /**
     * @description - This method is used to search the hierarchy templates data 
     * from the server and update the state with the response data
     * @param {Object} event - event object
     * @param {Boolean} search - search flag
     * @returns {void}
     */
    searchTemplate = (event) => {
        const data = [...this.data];
        const searchValue = event.detail.value?.trim()?.toLowerCase();
        const searchData = this.createSearchData(searchValue, data);
        this.totalCount = searchData.length;
        this.setState({tableData: searchData});
    }

    /**
     * Creates a search data based on the provided search value.
     * This method filters the data based on the provided search value and returns the filtered data.
     * @param {String} searchValue - The search value to be used for filtering the data.
     * @param {Array} data - The data to be filtered based on the provided search value.
     * @returns {Array} The filtered data based on the provided search value.
     */
    createSearchData = (searchValue, data) => {
        let searchData = [...data];
        const lowerCaseValue = (value) => value?.trim()?.toLowerCase();
        // Filter the data based on the search value by column wise and return all the matched data
        searchData = data.filter(item => lowerCaseValue(item['templateName']).includes(searchValue)
            || lowerCaseValue(item['mediaOpsState']).includes(lowerCaseValue(searchValue))
            || ((['active', 'inactive'].includes(lowerCaseValue(searchValue))) && item['isActive'] === (lowerCaseValue(searchValue) === 'active'))
            || lowerCaseValue(item['updatedBy'] || item['createdBy']).includes(searchValue)
            || lowerCaseValue(Utils.formatDateObject(item['campaignStart'])).includes(searchValue)
            || lowerCaseValue(Utils.formatDateObject(item['campaignEnd'])).includes(searchValue)
            || lowerCaseValue(Utils.formatDateObject((item['updatedAt'] || item['createdAt']))).includes(searchValue)
        );
        return searchData;
    };

    /**
     * @description - Applies a filter based on the provided value.
     * If the value object is empty, fetches hierarchy templates data.
     * Otherwise, performs a search on the templates using the provided value.
     * @param {Object} value - filter object
     * @returns {void}
     */
    applyFilter = (value) => {
        const isAvailable = Object.keys(value).length
        if(isAvailable === 0){
            this.totalCount = this.data.length;
            this.setState({tableData: this.data})
        }else{
            const filteredData = this.createFilterData(value);
            this.totalCount = filteredData.length;
            this.setState({tableData: filteredData});
        }
    }

    /**
     * Creates a filter data based on the provided filter object.
     * This method filters the data based on the provided filter object and returns the filtered data.
     * @param {Object} filterObject - The filter object to be used for filtering the data.
     * @returns {Array} The filtered data based on the provided filter object.
     */
    createFilterData = (filterObject) => {
        let filterData = [...this.data];
        const lowerCaseValue = (value) => value?.trim()?.toLowerCase();
        
        Object.keys(filterObject).forEach(key => {
            if(key === 'status'){
                filterData = filterData.filter(item => item['isActive'] === (filterObject[key] === 'Active'));
            } else if(key === 'templateName' || key === 'mediaOpsState'){
                filterData = filterData.filter(item => lowerCaseValue(item[key]).includes(lowerCaseValue(filterObject[key])));
            } else if(key === 'campaignStart' || key === 'campaignEnd'){
                filterData = filterData.filter(item => Utils.formatDateObject(item[key]) === filterObject[key]);
            } else if(key === 'updatedAt'){
                filterData = filterData.filter(item => Utils.formatDateObject(item[key] || item['createdAt']) === filterObject[key]);
            } else if(key === 'updatedBy'){
                filterData = filterData.filter(item => lowerCaseValue(item['updatedBy'] || item['createdBy']).includes(lowerCaseValue(filterObject[key])));
            };
        });
        return filterData;
    };

  /**
     * Copies a template based on the provided value.
     * This function first sets the spinner count to 1, indicating a loading state.
     * It then creates a payload from the provided value and attempts to add a new hierarchy template.
     * If the operation is successful, it fetches the updated hierarchy templates data, shows a success message, and sets the spinner count back to 0.
     * If an error occurs, it shows an error message and sets the spinner count back to 0.
     * @param {Object} value - The template object to be copied.
     * @returns {void}
     */
    copyTemplate = (value) => {
        this.props.setSpinnerCount(1);
        ObjectsHierarchyService.addHierarchyTemplate(this.createCopyTemplatePayload(value))// Attempt to add the new hierarchy template
            .then(() => {
                this.fetchHierarchyTemplatesData();
                AlertMessagesService.showSuccessToastMessage('Template copied successfully');
                this.props.setSpinnerCount(0);
            })
            .catch(() => {
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.copyTemplate(value), title: 'Unable to copy the template', bodyMsgArr: [] }]);
                this.props.setSpinnerCount(0);
            });
    };

    /**
     * Creates a payload for copying a template based on the provided value.
     * @param {Object} value - The template object to be copied.
     * @returns {Array} The payload array for copying the template.
     */
    createCopyTemplatePayload = (value) => {
        // Destructure the template object to exclude certain properties
        const { id, hierarchyObjects, templateName, ...rest } = value;
        return [
            {
                ...rest,
                templateName: templateName + " copy", // Append "copy" to the template name
                mediaOpsState: 'Saved', // Set the media operations state to 'Saved'
            }
        ];
    };

    /**
     * Deletes a template based on the provided value.
     * This function first sets the spinner count to 1, indicating a loading state.
     * It then attempts to delete the hierarchy template using the provided value.
     * If the operation is successful, it fetches the updated hierarchy templates data, shows a success message, and sets the spinner count back to 0.
     * If an error occurs, it shows an error message and sets the spinner count back to 0.
     * @param {Object} value - The template object to be deleted.
     * @returns {void}
     */
    deleteTemplate = (value) => {
        this.props.setSpinnerCount(1);
        ObjectsHierarchyService.deleteHierarchy(value.id) // Attempt to delete the hierarchy template using the provided value
            .then(() => {
                this.fetchHierarchyTemplatesData();
                AlertMessagesService.showSuccessToastMessage('Template deleted successfully');
                this.props.setSpinnerCount(0);
            })
            .catch(() => {
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.deleteTemplate(value), title: 'Unable to delete the template', bodyMsgArr: [] }]);
                this.props.setSpinnerCount(0);
            });
    };

    // navigates to specific path using Utils.navigateToComponent()
    navigateToPath = (value, path) => {
        this.props.setSpinnerCount(1);
        Utils.navigateToComponent({ row: value, columnOptions: this.columnOptions }, path, this.props)
    }

    /**
     * @description - This method is used to update the sort column
     * @param {Object} event - event object
     * @returns {void}
     */
    updateSortColumn = (event) => {
        let sortedData = [...this.state.tableData]

        // Updating `this.sort` with any new or changed properties.
        Object.assign(this.sort, event.detail);

        if (event.detail.dir === 'asc') {
            if (['campaignStart', 'campaignEnd', 'updatedAt'].includes(event.detail.key)) {
                sortedData = Utils.sortDateValues(event, sortedData);
            } else {
                sortedData = Utils.sortStringAndBooleanValues(event, sortedData);
            }
        } else if (event.detail.dir === 'desc') {
            sortedData = sortedData.reverse();
        }
        this.setState({ tableData: sortedData });
    }

    /**
     * @description - This method is used to get the status of the campaign
     * @param {Object} value - campaign object
     * @returns {Object} - returns the status of the campaign
     */
    getStatus = (value) => {
        let color = '';
        let content = value?.isActive ? 'Active' : 'Inactive';
        switch (content) {
            case 'Active':
                color = 'var(--text-success)'
                break;
            case 'Inactive':
                color = 'var(--text-error)'
                break;
            default:
                break;
        }
        return html`<td part="table-body-cell"><span part="export-status" style="color:${color};">${content}</span></td>`
    }

    /**
     * @description - This method is used to get the date of the campaign
     * @param {Object} value - campaign object
     */
    getDate = (value) => {
        const date = value && value.includes('T') ? value.split('T')[0] : value === 'string' ? '' : value;
        return html`<td part="table-body-cell"> <span part="td-content">${Utils.formatDateObject(date)}</span></td>`
    }

    /**
     * @description - This method is used to get the actions of the campaign
     * @param {Object} value - campaign object
     * @returns {Object} - returns the actions of the campaign
    */
    getActions = (value) => {
        return html`
                <td part="table-body-cell">
                    <div part="actions">
                        ${this.editButton(value)}
                        ${this.copyButton(value)}
                        ${this.deleteButton(value)}
                    </div>
                </td>`;
    };

    // Edit button to edit the template data
    editButton = (value) => {
        return html`<omni-tooltip-next>
                    <button slot="invoker" aria-haspopup="true" class="icon" @click=${this.navigateToPath.bind(this, value, 'objectsHierarchyDetails')}>
                        <omni-icon class="is-size-1" icon-id="omni:interactive:edit"></omni-icon>
                    </button>
                    <div slot="content">Edit template</div>
                </omni-tooltip-next>`
    };

    // Copy button to copy the template data
    copyButton = (value) => {
        return html`<omni-tooltip-next>
                        <button slot="invoker" aria-haspopup="true" class="icon" @click=${() => this.copyTemplate(value)}>
                            <omni-icon class="is-size-1" icon-id="omni:interactive:copy"></omni-icon>
                        </button>
                        <div slot="content">Copy template</div>
                    </omni-tooltip-next>`
    };

    // Delete button to delete the template data
    deleteButton = (value) => {
        //isActive false is the inactive template
      
        if (!value?.isActive || value.clientName === 'default') {
            return '';
        } else {
            return html`
                <omni-tooltip-next>
                    <button slot="invoker" aria-haspopup="true" class="icon" @click=${() => this.deleteTemplate(value)}>
                        <omni-icon class="is-size-1" icon-id="omni:interactive:delete"></omni-icon>
                    </button>
                    <div slot="content">Delete template</div>
                </omni-tooltip-next>
            `;
        }
    };

    /**
     * @description - This method is used to check if the column is sortable
     * @param {String} columnName - column name
     * @returns {Boolean} - returns true if the column is sortable
     */
    checkIfColumnIsSortable = (columnName) => {
        if(columnName === 'updatedAt') {
            return !this.state?.tableData?.every(item => ['', null, undefined].includes(item[columnName] || item?.createdAt));
        } else if(columnName === 'updatedBy') {
            return !this.state?.tableData?.every(item => ['', null, undefined].includes(item[columnName] || item?.createdBy));
        } else {
            return !this.state?.tableData?.every(item => ['', null, undefined].includes(item[columnName]));
        }
    };

    objectsHierarchyColumns = () => 
        {
            return[
        {
            label: 'TEMPLATE NAME',
            key: 'templateName',
            passthrough: true,
            isSortable: this.checkIfColumnIsSortable('templateName'),
            template: (value) => {
                let elementPart = value.clientName == 'default' ? 'table-body-cell default-template' : 'table-body-cell template-name';
                return html`<td part=${elementPart}>${value.templateName}</td>`
            }
        },
        {
            label: 'Mediaops State',
            key: 'mediaOpsState',
            passthrough: true,
            isSortable: this.checkIfColumnIsSortable('mediaOpsState'),
            template: (value) => { 
                return html`
                <style>
                    :host::part(td-content) {
                        color: var(--color-grey-600) !important;
                    }
                </style>
                <td part="table-body-cell"> <span part="td-content">${value.mediaOpsState}</span></td>` 
            }
        },
        {
            label: 'START DATE',
            key: 'campaignStart',
            passthrough: true,
            isSortable: this.checkIfColumnIsSortable('campaignStart'),
            template: (value) => { return this.getDate(value.campaignStart) }
        },
        {
            label: 'END DATE',
            key: 'campaignEnd',
            passthrough: true,
            isSortable: this.checkIfColumnIsSortable('campaignEnd'),
            template: (value) => { return this.getDate(value.campaignEnd) }

        },
        {
            label: 'MODIFIED BY',
            key: 'updatedBy',
            passthrough: true,
            isSortable: this.checkIfColumnIsSortable('updatedBy'),
            template: (value) => { return html`<td part="table-body-cell"> <span part="td-content">${value.updatedBy || value.createdBy}</span></td>` }
        },
        {
            label: 'MODIFIED DATE',
            key: 'updatedAt',
            passthrough: true,
            isSortable: this.checkIfColumnIsSortable('updatedAt'),
            template: (value) => { return this.getDate(value.updatedAt || value.createdAt) }
        },

        {
            label: 'STATUS',
            key: 'status',
            passthrough: true,
            isSortable: this.checkIfColumnIsSortable('isActive'),
            template: (value) => { return this.getStatus(value) }
        },

        {
            label: 'ACTIONS',
            key: 'actions',
            passthrough: true,
            template: (value) => {
                return this.getActions(value)
            }
        }
    ];
}

    render() {
        return (<>
            <div className="objects-hierarchy-container">
                    <OmniTileComponent heading="">
                        <OmniToolbarComponent slot="header" className='header-toolbar'>
                            <label slot="start">Objects hierarchy templates</label>
                            <div slot="end" className="custom-dropdown-menu header-menu">
                                <OmniSearchComponent
                                    onSearchUpdate={this.searchTemplate}
                                    ph="Search"
                                />
                                <div className="Line-Copy-2"></div>
                                <HierarchyFilterComponent
                                    onApplyFilter={this.applyFilter}
                                />
                            </div>
                        </OmniToolbarComponent>
                    </OmniTileComponent>

                    {!this.state.showSkeleton ?
                        this.totalCount > 0 ?
                            <>
                                <OmniTableComponent
                                    sort={this.sort}
                                    keyFn={row => row}
                                    id="objectsHierarchyTable"
                                    data={this.state.tableData}
                                    key={this.state.tableData.length}
                                    onSortUpdate={this.updateSortColumn}
                                    columns={this.objectsHierarchyColumns()}
                                />
                                <div className="objects-hierarchy-footer">
                                    <span>
                                        Showing 1-{this.totalCount} of {this.totalCount}
                                    </span>
                                </div>
                            </>
                            :
                            <NoDataComponent
                                heading={"No Data Found"}
                                message={"No hierarchy templates found!"}
                            />
                        : this.displaySkeletonTable()
                    }
            </div>
        </>)
    }
}